import cn from 'classnames';
import { forwardRef } from 'react';

import { useFieldId } from '../../../utils';
import { Autocomplete, BaseField, FieldLabel } from '../../internal';

import type { ForwardedRef, ReactElement } from 'react';

import type { AutocompleteFieldComponentType, AutocompleteFieldProps } from './autocomplete-field.types';

const AutocompleteField = forwardRef(
  <AutocompleteOption extends NonNullable<unknown>, Multiple extends boolean = false, FreeSolo extends boolean = false>(
    props: AutocompleteFieldProps<AutocompleteOption, Multiple, FreeSolo>,
    forwardedRef: ForwardedRef<HTMLElement>,
  ): ReactElement => {
    const {
      autoSelect,
      className,
      clearable,
      closeOnSelect,
      defaultValue,
      disabled,
      error,
      filterOptions,
      freeSolo,
      fullWidth,
      getLimitTagsText,
      getOptionDisabled,
      getOptionLabel,
      groupBy,
      helperText,
      hideSelectedOptions,
      id,
      inputValue,
      label,
      leftElement,
      limitTags,
      loading,
      loadingText,
      maxRows,
      multiple,
      noOptionsText,
      onChange,
      onClose,
      onInputChange,
      onOpen,
      onPaste,
      options,
      pasteDelimiter,
      placeholder,
      readOnly,
      renderOption,
      required,
      rightElement,
      selectAll,
      selectAllLabel,
      tooltip,
      value,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const controlId = useFieldId(id, 'rbui-autocomplete-field');

    const autocompleteProps = {
      autoSelect,
      clearable,
      closeOnSelect,
      defaultValue,
      disabled,
      error,
      filterOptions,
      freeSolo,
      getLimitTagsText,
      getOptionDisabled,
      getOptionLabel,
      groupBy,
      hideSelectedOptions,
      id: controlId,
      inputValue,
      leftElement,
      limitTags,
      loading,
      loadingText,
      maxRows,
      multiple,
      name: undefined,
      noOptionsText,
      onChange,
      onClose,
      onInputChange,
      onOpen,
      onPaste,
      options,
      pasteDelimiter,
      placeholder,
      readOnly,
      renderOption,
      required,
      rightElement,
      selectAll,
      selectAllLabel,
      value,
    };

    const fieldLabel = label && (
      <FieldLabel htmlFor={controlId} required={required} tooltip={tooltip}>
        {label === ' ' ? <span>&nbsp;</span> : label}
      </FieldLabel>
    );

    return (
      <BaseField
        className={cn(className, 'rbui-autocomplete-field')}
        control={<Autocomplete {...autocompleteProps} ref={forwardedRef} />}
        error={error}
        fullWidth={fullWidth}
        helperText={helperText}
        label={fieldLabel}
        {...rest}
      />
    );
  },
);

AutocompleteField.displayName = 'AutocompleteField';

/**
 * The `AutocompleteField` component provides a complete field layout for the Autocomplete component,
 * including label, helper text, and tooltip support. It allows users to search for value(s) to select
 * from a list with enhanced form integration.
 *
 * ### Usage
 *
 * ```tsx
 * import { AutocompleteField } from '@bloomreach/react-banana-ui';
 *
 * export default function MyAutocompleteField() {
 *   return (
 *     <AutocompleteField
 *       label="Select a fruit"
 *       helperText="Choose your favorite fruit"
 *       options={['Banana', 'Apple', 'Orange', 'Pineapple', 'Strawberry', 'Grape', 'Watermelon']}
 *       placeholder="Search fruits..."
 *       onChange={(_, newValue) => console.log(newValue)}
 *     />
 *   );
 * }
 * ```
 */
export default AutocompleteField as AutocompleteFieldComponentType;
